import type { HostPaymentTypeDTO } from 'data-types';
// ============================================================================
// Handle Get Request Types
// ============================================================================

// refer data-types/src/CustomerTypes.ts

export interface CustomerData {
  first_name: string;
  last_name: string;
  email: string;
  age: number;
  gender: number;
  paidOrdersCount: number;
}

export interface OrderAddresses {
  country_id: string;
  city: string;
  street: string;
  zip: string;
  house_number: string;
  phone: string;
}

export interface CartData {
  cartID: string;
  cartToken: string;
  mention: string;
  mixpanelTitle: string;
  timekitBooking: any;
  country: string;
}

export interface OrderWindowDetails {
  itemImageUrl: string;
  itemTitle: string;
  itemQuantity: string;
  treatmentFee: number;
  Price: number;
  serviceFee: number;
  isSubsidized: boolean;
  shippingFee: number;
  discount: number;
  bonusValue: number;
  isExpressShipping: boolean;
  isSelfPickup: boolean;
  isCannabisIDAddedToOrder: boolean;
  disclaimer: string;
  cannabisIdCardCost: number;
  onlyPrescription: number;
  orderShippingType?: string;
}


// ============================================================================
// Payment Result Types
// ============================================================================

export type PaymentResultCode = 'Authorised' | 'Pending' | 'Received' | 'Refused' | 'Cancelled' | 'UnknownCode';

export interface PaymentResult {
  resultCode: PaymentResultCode;
  status?: string; // e.g. 'recent_pending_payment': the previous request for this order is still being handled
  isUpgradedToPremium?: boolean;
  paymentMethod?: { type: string } | string; // Primer sends the method type as a plain string
  return_url: string;
  
  // Late payment
  isExpressUnavailable?: boolean;
  latePaymentReason?: string;
  isNextDayAvailable?: boolean;
  
  // Express prescription review unavailable
  isExpressPrescriptionUnavailable?: boolean;
  
  // output from the payment provider
  providerResponse: {
    action: any; // Adyen-specific
    
    // Stripe-specific
    status: string;
    client_secret: string;
  };
  
  // ======================
  // Adyen-specific fields
  // ======================
  message?: string;
  refusalReasonCode?: string;
  refusalReason?: string;
  
  // ======================
  // Stripe-specific fields
  // ======================
  stripeError?: {
    code?: string;           // e.g. 'card_declined', 'expired_card', 'incorrect_cvc'
    decline_code?: string;   // e.g. 'insufficient_funds', 'lost_card', 'stolen_card'
    message?: string;        // Human-readable error message
  };

  // ======================
  // Primer-specific fields
  // ======================
  provider?: PaymentProvider;
  primerPaymentId?: string;
  requiredAction?: { clientToken: string }; // 3DS / redirect: hand the token back to Universal Checkout
  claimedProvider?: PaymentProvider;        // the processor Primer's Workflow routed the payment to
  primerError?: PrimerError;

  // Manage authorization state
  isAuthorized?: boolean;
}

export interface PaymentErrorResponse {
  status: string;
  message?: string;
  logLevel?: string;
  cartData?: CartData;
  code?: string;
  
  // Stripe error details
  stripeError?: {
    code?: string;
    decline_code?: string;
    message?: string;
  };

  // Primer error details
  primerError?: PrimerError;
}

export interface PrimerError {
  code?: string;          // Primer decline code, e.g. 'INSUFFICIENT_FUNDS', 'DO_NOT_HONOR'
  declineType?: string;   // 'SOFT_DECLINE' | 'HARD_DECLINE'
  message?: string;
}


// ============================================================================
// Payment Context & Callbacks
// ============================================================================
export type PaymentProvider = 'adyen' | 'stripe' | 'stripe_gb' | 'primer';

export const STRIPE_PROVIDERS: PaymentProvider[] = ['stripe', 'stripe_gb'];
export const isStripeProvider = (code: string): boolean => STRIPE_PROVIDERS.includes(code as PaymentProvider);
export const isPrimerProvider = (code: string): boolean => code === 'primer';

// Mixpanel `checkout_variant` (Primer A/B test): the Primer checkout vs the direct PSPs (Adyen, Stripe).
export type CheckoutVariant = 'primer' | 'legacy';
export const checkoutVariantForProvider = (code: string | null | undefined): CheckoutVariant =>
  isPrimerProvider(code ?? '') ? 'primer' : 'legacy';

export interface PaymentContext {
  hashID: string;
  country: string;
  language: string;
  currencyCode: string;
  currency: string;
  orderPrice: number;
  currencyRate: number;
  itemTitle: string;
  force3DS: string | null;
  isRepay: boolean;
  mailCode: string | null;
  orderIds: number[];
  orderHashIDs: string[];
  isCannabisCatalog: boolean;
  thankyouHash: string;
  customerData: CustomerData;
  catalog: { mention: string };
  isOrderOnlyPrescription: number;
  paymentServiceProvider: PaymentProvider;
  checkoutProvider?: 'primer' | null; // set when the checkout UI is Primer's rather than the PSP's own
  sepaPositionVariant: string;
  stripeLinkVariant?: 'A' | 'B';
  cartExperiments?: Record<string, string> | null;
  // Set by CheckoutStack when the page has several checkout sections: makePayment reports the
  // attempt so only one section submits at a time.
  onPaymentStart?: () => void;
  onPaymentEnd?: () => void;
}

export interface PaymentCallbacks {
  onPaymentSuccess: (result: PaymentResult) => void;
  onPaymentRefused: (result: PaymentResult) => void;
  onPaymentAction: (action: any) => Promise<void>;
  onExpressUnavailable: (reason: string, isNextDayAvailable: boolean) => void;
  onExpressPrescriptionReviewUnavailable: () => void;
  onError: (error: any) => void;
  showErrorNotification: (title: string, content: string, options?: { sticky?: boolean }) => void;
  setPaymentRefused: (refused: boolean) => void;
  onOutOfStock: (cartData: CartData) => void;
  onRefreshPage: () => void;
  onIdVerificationRequired?: (paymentMethodType?: string) => void;
  onTriggerPaymentSubmit?: () => void;
  isIdVerified?: boolean;
  verificationCollapseKey?: number;
  paymentTypesRequiringIdVerification?: string[];
  idVerificationPaymentMethodNames?: Record<string, string>;
  isExpressOrder?: boolean;
  hidePaypalDisclaimer?: boolean;
}


// ============================================================================
// Provider-specific Init Data
// ============================================================================

export interface AdyenInitData {
  gatewayResult: any;
  reorderedGatewayResult?: any;
  clientKey: string;
  environment: "test" | "live" | "live-us" | "live-au" | "live-apse" | "live-in" | undefined;
  isSubscription: boolean;
  
  customerData: CustomerData;
  orderAddresses: OrderAddresses;
  reorderedPaymentMethods: boolean;
}

export interface StripeInitData {
  gatewayResult: any;

  stripePublicKey: string;
  customerSessionSecret: string;
  isSubscription: boolean;

  customerData: CustomerData;
  orderAddresses: OrderAddresses;
}

export interface PrimerInitData {
  clientToken: string;
  environment: 'TEST' | 'LIVE';
  currency: string;
  paymentMethods: string[]; // Primer paymentMethodTypes allowed for this order
  isSubscription: boolean;

  customerData: CustomerData;
  orderAddresses: OrderAddresses;

  // Primer's methods with their payment_types order and title (flat method list)
  hostPaymentTypes?: HostPaymentTypeDTO[];
}



// ============================================================================
// Tracking Types
// ============================================================================

export interface UserClickTracking {
  openedTooltips: Set<string>;
  userClicks: Set<string>;
}


// ============================================================================
// Secondary Payment Types (payment_v2 secondaryPaymentTypes / secondaryProviders)
// ============================================================================

// A payment type the order's PSP shows as a tile, processed by another PSP (or by itself).
export type { HostPaymentTypeDTO, SecondaryPaymentTypeDTO } from 'data-types';
